/// FOURJS_START_COPYRIGHT(D,2014)
/// Property of Four Js*
/// (c) Copyright Four Js 2014, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

/* We need to wait for translation service to init,
 * As some debug widgets need translation
 */
modulum("DebugService", ["InitService", "I18NService"], function (context, cls) {
  /**
   * Debug Service
   * @namespace gbc.DebugService
   * @gbcService
   */
  class DebugService {
    constructor() {
      /** @type Window */
      this._monitorWindow = null;
      /**
       * @type classes.MonitorWidget
       */
      this._widget = null;
      this._folder = null;
      this._vmLogs = null;
      this._proxyLogs = null;

      /**
       * @type classes.DebugAuiController
       */
      this._debugAuiController = null;

      this._isDebugWindow = false;
      /**
       * @type classes.EventListener
       */
      this._eventListener = null;

      this._debugUis = null;
      this._active = false;
      this._disabled = false;

      this._highlightElement = null;
      this._highlightTimer = null;
      this._highlightDisplayTime = null;

      this._gridHighlightColors = null;

      this._nodeToShow = null;
      this._persistentDebugGrid = false;

      this._orderManagedEvent = null;
    }

    /**
     * Check if the current page is a debug monitor
     * @return {boolean} - true if it's the debug monitor, false otherwise
     * @private
     */
    _isMonitor() {
      return Boolean(context.UrlService.currentUrl().getQueryStringObject().monitor);
    }

    /**
     * Init service method. should be called only once.
     */
    init() {
      this._debugUis = [];
      this._eventListener = new cls.EventListener();
      this._highlightDisplayTime = 2000;
      // Set default style of debug grid highlight
      this._gridHighlightColors = {
        color1: "rgba(255,100,0,0.2)",
        color2: "rgba(255,0,0,0.2)"
      };

      if (this._isMonitor()) {
        this._isDebugWindow = true;
        this._debugAuiController = new cls.DebugAuiController();

        this._monitor = cls.WidgetFactory.createWidget("Monitor", {
          appHash: gbc.systemAppId
        });

        this._folder = cls.WidgetFactory.createWidget("Folder", {
          appHash: gbc.systemAppId
        });
        this._folder.isMinified = true;
        const auiTree = this._createAuiTreePage();
        const pageProxy = this._createProxyPage();
        const pageVm = this._createVmPage();
        this._folder.addChildWidget(auiTree);
        this._folder.addChildWidget(pageProxy);
        this._folder.addChildWidget(pageVm);
        // Due to a weird rendering, the vm content is displayed and should active
        // to then be set inactive
        this._folder.setCurrentPage(pageVm);
        this._folder.setCurrentPage(auiTree);

        const containerElement = document.createElement("div");
        containerElement.classList.add("tab-folder-debug");
        containerElement.appendChild(this._monitor.getElement());
        containerElement.appendChild(this._folder.getElement());

        document.body.appendChild(containerElement);

        window.setTimeout(
          function () {
            // Getting correct gbc's instance to inspect
            const rootGbc = window._multiWindowData.parentWindow?.gbc || context.BrowserWindowsService.getRootGbc(),
              session = rootGbc.SessionService.getCurrent(),
              app = session?.getCurrentApplication();
            if (app) {
              this._debugAuiController._appInfo = gbc.info(app.applicationInfo);
              this._debugAuiController._sessionId = app.getSession()._sessionId;
              this._debugAuiController.refreshDebugAui(app.getNode(0));
              this._vmLogs.loadDebugContent(app.getSession());
              this._proxyLogs.loadDebugContent(app.getSession());
              rootGbc.DebugService.attach(window);
              // Refresh debug window at each order
              this._onAppOrdersManaged(
                app,
                function () {
                  this._debugAuiController.refreshDebugAui(app.getNode(0));
                }.bind(this)
              );
            }
          }.bind(this),
          100
        );
      } else if (context.__wrapper.isPlatformTypeNative()) {
        context.__wrapper.on(
          context.__wrapper.events.DEBUGNODE,
          function (event, src, nodeId) {
            this.onHighlightAuiNode({
              auiNodeId: nodeId
            });
          }.bind(this)
        );
      }
      this.whenActivationChanged((_event, _src, active) => {
        if (active) {
          document.body.classList.add("gbc_DebugMode");
          if (cls.WindowHelper.isURLParameterEnabled(window.location.search, "debugcounter")) {
            this.tryCounterServer();
          }
        }
      });
    }

    _createAuiTreePage() {
      const auiPage = cls.WidgetFactory.createWidget("Page", {
        appHash: gbc.systemAppId
      });
      auiPage.setTitle(gbc.I18NService.t("gwc.main.chromebar.auiTree"));
      auiPage.setText(gbc.I18NService.t("gwc.main.chromebar.auiTree"));
      auiPage.addChildWidget(this._debugAuiController.getWidget());
      return auiPage;
    }

    _createProxyPage() {
      const proxyPage = cls.WidgetFactory.createWidget("Page", {
        appHash: gbc.systemAppId
      });
      proxyPage.setTitle(gbc.I18NService.t("gwc.main.chromebar.proxyLog"));
      proxyPage.setText(gbc.I18NService.t("gwc.main.chromebar.proxyLog"));
      this._proxyLogs = cls.WidgetFactory.createWidget("MonitorDebugProxyLogs", {
        appHash: gbc.systemAppId
      });
      proxyPage.addChildWidget(this._proxyLogs);
      return proxyPage;
    }

    _createVmPage() {
      const vmPage = cls.WidgetFactory.createWidget("Page", {
        appHash: gbc.systemAppId
      });
      vmPage.setTitle(gbc.I18NService.t("gwc.main.chromebar.vmLog"));
      vmPage.setText(gbc.I18NService.t("gwc.main.chromebar.vmLog"));
      this._vmLogs = cls.WidgetFactory.createWidget("MonitorDebugVmLogs", {
        appHash: gbc.systemAppId
      });
      vmPage.addChildWidget(this._vmLogs);
      return vmPage;
    }

    /**
     * Handler called once application has switched
     */
    onApplicationSwitch() {
      if (this._monitorWindow) {
        const debugAuiController = this._monitorWindow.gbc.DebugService._debugAuiController;
        const app = gbc.SessionService.getCurrent().getCurrentApplication();

        // Refresh debug window at each order
        this._onAppOrdersManaged(app, () => {
          debugAuiController.refreshDebugAui(app.getNode(0));
        });

        debugAuiController.refreshDebugAui(app.getNode(0));
        this.attach(this._monitorWindow);
      }
      this.hideHighlightAui();
    }

    /**
     * @return {boolean}
     */
    isMonitorWindow() {
      return this._isDebugWindow;
    }

    /**
     * Destroy the service
     */
    destroy() {
      if (this._highlightTimer) {
        window.clearTimeout(this._highlightTimer);
        this._highlightTimer = null;
      }
      if (this._monitorWindow) {
        this._monitorWindow.close();
      }
      if (this._orderManagedEvent) {
        this._orderManagedEvent(); //unbind events
      }
    }

    /**
     * Show the Debug window with the AUI tree
     * @param {Number} [auiId] the aimed node id
     */
    show(auiId = -1) {
      if (!this._monitorWindow) {
        context.__wrapper.showDebugger(context.__wrapper.param(auiId, context.SessionService.getCurrent().getCurrentApplication()));
      } else {
        const uiNode = context.SessionService.getCurrent().getCurrentApplication().getNode(0);
        const debugAuiController = this._monitorWindow.gbc.DebugService._debugAuiController;
        if (uiNode.auiSerial !== debugAuiController.auiSerial) {
          debugAuiController.refreshDebugAui(uiNode);
        }
        this._monitorWindow.focus();
      }
    }

    /**
     *
     * @param monitorWindow
     */
    attach(monitorWindow) {
      if (this._monitorWindow !== monitorWindow) {
        this._monitorWindow = monitorWindow;
        this._monitorWindow.document.title = "GBC Debug tools";
        this._monitorWindow.onunload = () => {
          this._monitorWindow = null;
        };
        //persistent Debug Grid
        cls.DOMElementHelper.on(this._monitorWindow.document.querySelector("#debugGrid"), "change.persistentDebugGrid", (event) => {
          gbc.DebugService.setPersistentDebugGrid(event.target.checked);
        });
        if (this._nodeToShow !== null) {
          this._monitorWindow.gbc.DebugService._debugAuiController.showNode(this._nodeToShow);
          this._nodeToShow = null;
        } else {
          this._monitorWindow.gbc.DebugService._debugAuiController.showNode(window.gbcNode(0));
        }
        this._monitorWindow.addEventListener(context.classes.DebugAuiController.highlightAui, this.onHighlightAuiNode.bind(this));
      }
    }

    /**
     * Enable/disable the persistent Debug grid
     * @param status {boolean} - true to enable, false otherwise
     */
    setPersistentDebugGrid(status) {
      this._persistentDebugGrid = status;
      if (!status && this._highlightElement) {
        this._highlightElement.remove();
        this._highlightTimer = null;
        this._highlightElement = null;
      }
    }

    /**
     * Define timeout before hiding debug highlight
     * @param time {number} - time to keep debug highlight on screen (in ms) - default: 2000ms
     */
    setHighlightDisplayTime(time) {
      this._highlightDisplayTime = time;
    }

    /**
     *
     * @param widget
     */
    registerDebugUi(widget) {
      if (this._debugUis.indexOf(widget) < 0) {
        this._debugUis.push(widget);
      }
      if (widget.activate) {
        widget.activate(this._active);
      }
    }

    /**
     *
     * @param widget
     */
    unregisterDebugUi(widget) {
      if (this._debugUis.indexOf(widget) >= 0) {
        cls.ArrayHelper.remove(this._debugUis, widget);
      }
    }

    /**
     *
     * @param hook
     * @return {*|HandleRegistration}
     */
    whenActivationChanged(hook) {
      return this._eventListener.when("debugActivationChanged", hook);
    }

    /**
     * This override the disabled mode
     */
    enable() {
      this._disabled = false;
    }

    /**
     *
     */
    disable() {
      this._disabled = true;
    }

    /**
     * Activate the Debug service
     * @param {Boolean} [force] - true to override the url parameters
     */
    activate(force = false) {
      if (force) {
        this.enable();
      }
      if (!this._active && !this._disabled) {
        this._active = true;
        for (const widget of this._debugUis) {
          if (widget.activate) {
            widget.activate(this._active);
          }
        }
        context.classes.DebugHelper.activateDebugHelpers();
        this._registerDebugContextMenu();
        document.body.classList.add("gbc_DebugMode");
        context.ChromeActionService.createUpdateGbcMenu();
        this._eventListener.emit("debugActivationChanged", true);
      }
    }

    /**
     *
     * @private
     */
    _registerDebugContextMenu() {
      if (!this._isMonitor() && !this.__debugContextMenuRegistered) {
        this.__debugContextMenuRegistered = true;
        window.addEventListener("contextmenu", (event) => {
          const modKey = navigator.userAgent.indexOf("Mac") !== -1 ? event.metaKey : event.ctrlKey;
          if (modKey && !event.shiftKey && !event.altKey) {
            const auiNode = window.gbcNode(event.target);
            if (auiNode) {
              this.show(auiNode.getId());
              if (this._monitorWindow) {
                const uiNode = auiNode.getApplication().getNode(0);
                const debugAuiController = this._monitorWindow.gbc.DebugService._debugAuiController;
                if (uiNode.auiSerial !== debugAuiController.auiSerial) {
                  debugAuiController.refreshDebugAui(uiNode);
                }
                this._monitorWindow.gbc.DebugService._debugAuiController.showNode(auiNode);
              } else {
                this._nodeToShow = auiNode;
              }
              cls.EventHelper.preventCancelableDefault(event);
            }
          }
        });
      }
    }

    /**
     * Handler called once received orders from the VM
     * @param app - vm application
     * @param callback - hook called once orders are received
     * @private
     */
    _onAppOrdersManaged(app, callback) {
      if (this._orderManagedEvent) {
        this._orderManagedEvent();
      }
      this._orderManagedEvent = app.dvm.onOrdersManaged(() => {
        callback();
      });
    }

    /**
     *
     * @return {boolean}
     */
    isActive() {
      return this._active;
    }

    /**
     * Hide the highlight layer
     */
    hideHighlightAui() {
      if (this._highlightElement) {
        this._highlightElement.classList.add("hidden");
      }
    }

    /**
     * Handler called when an element is clicked in AUI tree
     * @param event
     */
    onHighlightAuiNode(event) {
      const currentSession = gbc.SessionService.getCurrent();
      const currentApp = currentSession && currentSession.getCurrentApplication();
      if (currentApp) {
        let node = currentApp.getNode(event.auiNodeId);
        if (!node) {
          return;
        }
        if (node.getTag() === "TreeItem") {
          let table = node;
          while (table.getTag() !== "Table") {
            table = table.getParentNode();
          }
          const valueIndex = node.attribute("row");
          if (valueIndex === -1) {
            return;
          }
          node = table.getFirstChild("TableColumn").getFirstChild("ValueList").getChildren()[valueIndex];
        }
        let widget = null;
        while (!widget) {
          const ctrl = node.getController();
          if (ctrl) {
            widget = ctrl.getWidget();
          }
          node = node.getParentNode();
        }

        if (this._highlightElement) {
          window.clearTimeout(this._highlightTimer);
          this._highlightElement.remove();
          this._highlightElement = null;
        }

        if (widget._layoutEngine) {
          if (widget._layoutEngine instanceof cls.GridLayoutEngine) {
            this._highlightElement = this.createGridHighlightElement(widget);
          } else if (widget._layoutEngine instanceof cls.DBoxLayoutEngine) {
            this._highlightElement = this.createDBoxHighlightElement(widget);
          }
        }
        if (!this._highlightElement) {
          this._highlightElement = this.createDefaultHighlightElement(widget);
        }

        document.body.appendChild(this._highlightElement);
        if (!this._persistentDebugGrid) {
          this._highlightTimer = window.setTimeout(
            function () {
              this._highlightElement.remove();
              this._highlightTimer = null;
              this._highlightElement = null;
            }.bind(this),
            this._highlightDisplayTime
          );
        }
      }
    }

    /*
     * Change style properties for grid highlight element
     * @param {Object} - style object with backgroundColor and border css
     */
    setGridHighlightColor(color1, color2) {
      this._gridHighlightColors = {
        color1,
        color2
      };
    }

    /**
     * Will create an element used to highlight a widget
     * @param widget
     * @return {HTMLElement}
     */
    createDefaultHighlightElement(widget) {
      const widgetRect = widget.getElement().getBoundingClientRect();
      const element = document.createElement("div");
      element.style.position = "fixed";
      element.style.backgroundColor = "rgba(255,0,0,0.5)";
      element.style.border = "1px solid red";
      element.style.zIndex = 999999;
      element.style.top = widgetRect.top + "px";
      element.style.left = widgetRect.left + "px";
      element.style.width = widgetRect.width + "px";
      element.style.height = widgetRect.height + "px";

      cls.DOMElementHelper.off(document.body, "keydown.debugLayer");
      cls.DOMElementHelper.on(document.body, "keydown.debugLayer", (event) => {
        // Hide debug layer on shift key down
        if (event.shiftKey) {
          element.style.zIndex = -5000;
          this._rebuildDebugLayer = true;
        }
      });

      cls.DOMElementHelper.off(document.body, "keyup.debugLayer");
      cls.DOMElementHelper.on(document.body, "keyup.debugLayer", (_event) => {
        // Rebuild debug layer on shift key up
        if (this._rebuildDebugLayer) {
          this._rebuildDebugLayer = false;
          this.onHighlightAuiNode({
            auiNodeId: widget._auiTag
          });
        }
      });

      // Rebuild debug layer on each layout
      const currentSession = gbc.SessionService.getCurrent();
      const currentApp = currentSession && currentSession.getCurrentApplication();
      if (currentApp) {
        currentApp.layout.afterLayout(
          this.onHighlightAuiNode.bind(this, {
            auiNodeId: widget._auiTag
          }),
          true
        );
      }

      return element;
    }

    /**
     * Will create an element used to highlight a grid widget
     * @param widget
     * @return {HTMLElement}
     */
    createGridHighlightElement(widget) {
      const widgetRect = widget.getElement().getBoundingClientRect();
      const element = this.createDefaultHighlightElement(widget);
      element.style.backgroundColor = "";
      element.classList.add("persistentDebugGrid");

      const decorating = {
        offsetLeft: widget.getLayoutInformation().getDecoratingOffset().getWidth(true),
        offsetTop: widget.getLayoutInformation().getDecoratingOffset().getHeight(true),
        width: widget.getLayoutInformation().getDecorating().getWidth(true),
        height: widget.getLayoutInformation().getDecorating().getHeight(true)
      };
      const dimensionElementsList = [
        widget._layoutEngine._xspace.dimensionManager.dimensionElements,
        widget._layoutEngine._yspace.dimensionManager.dimensionElements
      ];

      const xSpace = document.createElement("div");
      xSpace.classList.add("xSpace");
      const ySpace = document.createElement("div");
      ySpace.classList.add("ySpace");

      for (let i = 0; i < dimensionElementsList.length; ++i) {
        const dimensionElements = dimensionElementsList[i];
        let total = 0;
        for (let j = 0; j < dimensionElements.length; ++j) {
          const dimensionElement = dimensionElements[j];
          const bandSize = dimensionElement.getSize(true, true);
          const band = document.createElement("div");
          band.style.position = "absolute";
          band.style.backgroundColor = j % 2 ? this._gridHighlightColors.color1 : this._gridHighlightColors.colors;
          band.classList.add("element");
          if (i === 0) {
            // X
            band.style.top = 0 + (decorating.offsetTop - 15) + "px";
            band.style.left = total + decorating.offsetLeft + "px";
            band.style.width = bandSize + "px";
            band.style.height = widgetRect.height - decorating.height + 30 + "px";
            xSpace.append(band);
          } else {
            // Y
            band.style.top = total + decorating.offsetTop + "px";
            band.style.left = 0 + (decorating.offsetLeft - 10) + "px";
            band.style.width = widgetRect.width - decorating.width + 20 + "px";
            band.style.height = bandSize + "px";
            ySpace.append(band);
          }
          total += bandSize;
          band.setAttribute("title", dimensionElement.toString());
        }
      }

      element.appendChild(ySpace);
      element.appendChild(xSpace);

      const widgets = widget._layoutEngine._registeredWidgets;
      const slots = widget._layoutEngine._registeredSlots;
      for (let i = 0; i < widgets.length; ++i) {
        const childWidget = widgets[i];
        const slotX = slots[childWidget.getUniqueIdentifier()].x;
        const slotY = slots[childWidget.getUniqueIdentifier()].y;
        const childWidgetRect = childWidget.getElement().getBoundingClientRect();
        const childRectElement = document.createElement("div");
        childRectElement.classList.add("slot");
        childRectElement.style.position = "fixed";
        childRectElement.style.border = "1px solid red";
        childRectElement.style.top = childWidgetRect.top + "px";
        childRectElement.style.left = childWidgetRect.left + "px";
        childRectElement.style.width = childWidgetRect.width + "px";
        childRectElement.style.height = childWidgetRect.height + "px";
        childRectElement.setAttribute("title", slotX.toString() + "\n--------\n" + slotY.toString());
        element.appendChild(childRectElement);
      }
      return element;
    }

    /**
     * Will create an element used to highlight a DBox widget
     * @param widget
     * @return {HTMLElement}
     */
    createDBoxHighlightElement(widget) {
      const widgetRect = widget.getElement().getBoundingClientRect();
      const element = this.createDefaultHighlightElement(widget);

      element.style.backgroundColor = "";

      const children = widget.getChildren();
      let total = 0;
      for (let i = 0; i < children.length; ++i) {
        const bandSize = widget._layoutEngine._getAllocatedSize(children[i]);
        const band = document.createElement("div");
        band.style.position = "absolute";
        band.style.backgroundColor = i % 2 ? "rgba(255,100,0,0.5)" : "rgba(255,0,0,0.5)";
        if (widget._layoutEngine instanceof cls.HBoxLayoutEngine) {
          band.style.top = 0;
          band.style.left = total + "px";
          band.style.width = bandSize + "px";
          band.style.height = widgetRect.height + "px";
        } else {
          band.style.top = total + "px";
          band.style.left = 0;
          band.style.width = widgetRect.width + "px";
          band.style.height = bandSize + "px";
        }
        total += bandSize;
        element.appendChild(band);
      }

      return element;
    }

    /**
     * Simulate a network latency by applying a delay between each network request
     * @param {number} delay Simulated delay in milliseconds
     */
    setNetworkDelay(delay) {
      context.SessionService?.getCurrent()?.getCurrentApplication().protocolInterface.setNetworkDelay(delay);
    }
  }

  gbc.DebugService = new DebugService();

  context.InitService.register(context.DebugService);
});
